import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExecutionResult } from '@lcs/shared/api';
import { ReportParametersUnion } from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { DashboardTileLayoutConfigurationInput, ReportParameters, TileViewModes } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { getCurrentUserId } from 'src/app/authentication/state/authentication.selector';
import { SingleGraphError } from 'src/app/common-components/errors/models/error-message';
import * as UserPreferenceActions from 'src/app/store/user-preferences/user-preferences.actions';
import {
   getSelectedDashboardUserPreference,
   getUserPreferences,
} from 'src/app/store/user-preferences/user-preferences.selectors';
import * as TileActions from '../../tiles/state/tile.actions';
import { tileLoad } from '../../tiles/state/tile.actions';
import * as AddTileActions from '../add-dashboard-tile/state/add-dashboard-tile.actions';
import { DashboardTile, VerboseTileIds } from '../models/dashboard-tile.model';
import { Dashboard } from '../models/dashboard.model';
import {
   GetDashboards,
   GetDashboards_dashboardsConnection_items,
   GetDashboards_dashboardsConnection_items_dashboardTilesConnection,
   GetDashboards_dashboardsConnection_items_dashboardTilesConnection_items_dashboardTileParametersConnection_items,
} from '../queries/generated/GetDashboards';
import { GetDashboardTiles_dashboardTilesConnection } from '../queries/generated/GetDashboardTiles';
import {
   SaveDashboards_dashboard,
   SaveDashboards_dashboard_dashboardTilesConnection,
} from '../queries/generated/SaveDashboards';
import { SaveDashboardTileLayoutConfigurations } from '../queries/generated/SaveDashboardTileLayoutConfigurations';
import { DashboardApiService } from '../services/dashboard.api.service';
import { DashboardGridLayoutItemConfigurationInput } from '../views/dashboard-view.component';
import * as DashboardActions from './dashboard.actions';
import { getSelectedDashboard } from './dashboard.selectors';

@Injectable()
export class DashboardEffects {
   dashboardEntered$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.pageEntered),
         withLatestFrom(
            this.store.pipe(select(getCurrentUserId)),
            this.store.pipe(select(getUserPreferences)),
            this.store.pipe(select(getSelectedDashboard))
         ),
         switchMap(([_action, currentUserId, userPreferences, dashboard]) => {
            if (currentUserId === null) return EMPTY;
            return this.dashboardService.getDashboards(currentUserId).pipe(
               mergeMap((response) => {
                  const dashboards = this.mapToDashboards(response);
                  const selectedDashboard =
                     dashboards.find((d) => d.dashboardId === dashboard?.dashboardId) ??
                     dashboards.find((d) => d.dashboardId === userPreferences?.SelectedDashboardId) ??
                     dashboards[0] ??
                     null;
                  return [
                     DashboardActions.pageLoadedSuccess({
                        dashboards,
                        selectedDashboard,
                     }),
                     ...(selectedDashboard?.dashboardTiles ?? []).map((dt) =>
                        tileLoad({ dashboardTileId: dt.dashboardTileId })
                     ),
                  ];
               }),
               catchError((error) => {
                  return of(DashboardActions.pageLoadedFailure(error));
               })
            );
         })
      );
   });

   loadFullscreen = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.openFullscreenTile),
         map((action) => {
            return tileLoad({ dashboardTileId: VerboseTileIds.fullscreenTile, isFullscreen: true });
         })
      );
   });

   loadPreview = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddTileActions.loadPreviewTile),
         map((action) => {
            return tileLoad({ dashboardTileId: VerboseTileIds.previewTile, forceViewModeRecalculation: true });
         })
      );
   });

   addDashboard = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.addDashboard),
         withLatestFrom(this.store.pipe(select(getCurrentUserId))),
         switchMap(([action, currentUserId]) => {
            if (currentUserId === null) return EMPTY;

            return this.dashboardService.addDashboard(action.name, currentUserId).pipe(
               map((dashboard) => {
                  return DashboardActions.addDashboardSuccess({
                     dashboard: this.mapToDashboard(dashboard.data.dashboard[0]),
                  });
               }),
               catchError((error) => {
                  return of(DashboardActions.addDashboardFailure(error));
               })
            );
         })
      );
   });

   deleteDashboard = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.deleteDashboard),
         withLatestFrom(this.store.pipe(select(getCurrentUserId)), this.store.pipe(select(getSelectedDashboard))),
         switchMap(([action, currentUserId, selectedDashboard]) => {
            if (currentUserId === null || selectedDashboard === null) return EMPTY;

            return this.dashboardService.deleteDashboard(selectedDashboard.dashboardId).pipe(
               map((dashboard) => {
                  return DashboardActions.deleteDashboardSuccess({ dashboardId: selectedDashboard.dashboardId });
               }),
               catchError((error) => {
                  return of(DashboardActions.deleteDashboardFailure(error));
               })
            );
         })
      );
   });

   reloadSelectedDashboard = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            DashboardActions.reloadSelectedDashboard,
            UserPreferenceActions.saveSelectedDashboardIdSuccess,
            DashboardActions.saveGridLayoutChangeSuccess,
            AddTileActions.addTileSuccess,
            DashboardActions.reloadDashboardAfterResize,
            //ADDING THIS EFFECT FOR SAVING A TILE ON EDIT, will be removed and single tile fresh will be implemented
            DashboardActions.updateDashboardTileOnSelectedDashboardById
         ),
         withLatestFrom(
            this.store.pipe(select(getCurrentUserId)),
            //this.store.pipe(select(getSelectedDashboard))
            this.store.pipe(select(getSelectedDashboardUserPreference))
         ),
         switchMap(([action, currentUserId, selectedDashboard]) => {
            if (currentUserId === null || !selectedDashboard) return EMPTY;
            return this.dashboardService.getDashboard(currentUserId, selectedDashboard).pipe(
               map((response) => {
                  return DashboardActions.reloadSelectedDashboardSuccess({
                     dashboard: this.mapToDashboards(response)[0],
                  });
               }),
               catchError((error) => {
                  return of(DashboardActions.reloadSelectedDashboardFailure(error));
               })
            );
         })
      );
   });

   reloadSelectedDashboardSuccess = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.reloadSelectedDashboardSuccess),
         withLatestFrom(this.store.pipe(select(getCurrentUserId))),
         mergeMap(([action, currentUserId]) => {
            if (currentUserId === null) return EMPTY;
            const tileLoadActions = (action.dashboard.dashboardTiles ?? []).map((dt) =>
               tileLoad({ dashboardTileId: dt.dashboardTileId })
            );
            return [...tileLoadActions];
         })
      );
   });

   reloadDashboardForNewLayout$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.reloadDashboardForNewLayout),
         mergeMap((action) => {
            return action.layout.map((l) =>
               tileLoad({ dashboardTileId: l.dashboardTileId, forceViewModeRecalculation: true })
            );
         })
      );
   });

   renameDashboard$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.renameDashboard),
         withLatestFrom(this.store.pipe(select(getCurrentUserId)), this.store.pipe(select(getSelectedDashboard))),
         switchMap(([action, currentUserId, selectedDashboard]) => {
            if (currentUserId === null || selectedDashboard === null) return EMPTY;

            return this.dashboardService.renameDashboard(action.name, selectedDashboard.dashboardId).pipe(
               map((dashboard) => {
                  return DashboardActions.renameDashboardSuccess({
                     dashboard: this.mapToDashboard(dashboard.data.dashboard[0]),
                  });
               }),
               catchError((error: SingleGraphError) => {
                  return of(DashboardActions.renameDashboardFailure(error));
               })
            );
         })
      );
   });

   saveGridLayoutChange$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.saveGridLayoutChange),
         withLatestFrom(this.store.pipe(select(getSelectedDashboard))),
         switchMap(([action, selectedDashboard]) => {
            const allowedGridLayoutChangeFields: (keyof DashboardGridLayoutItemConfigurationInput)[] = [
               'w',
               'h',
               'x',
               'y',
            ];

            const dirtyItems = action.dashboardTileLayoutConfigurationInput.filter((dtlc) => dtlc.isDirty);
            const dirtyFields: Set<string> = new Set();

            const mappedDirtyItems: DashboardTileLayoutConfigurationInput[] = dirtyItems.map((lci) => {
               return {
                  dashboardTileLayoutConfigurationId: lci.id,
                  height: lci.h,
                  width: lci.w,
                  column: lci.x,
                  row: lci.y,
                  fields: lci.fields
                     .filter((f) => allowedGridLayoutChangeFields.some((af) => af === f))
                     .map((f) => {
                        let fVal = '';
                        switch (f) {
                           case 'h':
                              fVal = 'height';
                              break;
                           case 'w':
                              fVal = 'width';
                              break;
                           case 'x':
                              fVal = 'column';
                              break;
                           case 'y':
                              fVal = 'row';
                              break;
                           default:
                              fVal = f;
                        }

                        dirtyFields.add(fVal);
                        return fVal;
                     }),
               };
            });

            const sizeChanges = mappedDirtyItems.filter((ddtlc) =>
               ddtlc.fields.some((f) => f === 'height' || f === 'width')
            );

            if (mappedDirtyItems.length === 0 || !selectedDashboard)
               return of(
                  DashboardActions.saveGridLayoutChangeSuccess({
                     dirtyDashboardTileLayoutConfigurations: [],
                     columnLayoutSize: selectedDashboard?.lastSavedColumnLayoutSize ?? 0,
                  })
               );

            return this.dashboardService
               .saveDashboardTileLayoutConfigurations(
                  mappedDirtyItems.map((ddtlc) => {
                     return { ...ddtlc, fields: [...dirtyFields] };
                  }),
                  selectedDashboard,
                  action.columnLayoutSize
               )
               .pipe(
                  mergeMap((response: ExecutionResult<SaveDashboardTileLayoutConfigurations>) => {
                     const tileLoadActions = response.data.saveDashboardTileLayoutConfigurations
                        .filter((ddt) =>
                           sizeChanges.some(
                              (d) => d.dashboardTileLayoutConfigurationId === ddt.dashboardTileLayoutConfigurationId
                           )
                        )
                        .map((ddt) =>
                           tileLoad({ dashboardTileId: ddt.dashboardTileId, forceViewModeRecalculation: true })
                        );

                     return [
                        DashboardActions.saveGridLayoutChangeSuccess({
                           dirtyDashboardTileLayoutConfigurations: response.data.saveDashboardTileLayoutConfigurations,
                           columnLayoutSize: action.columnLayoutSize,
                        }),
                        ...tileLoadActions,
                     ];
                  }),
                  catchError((error: SingleGraphError) => {
                     return of(DashboardActions.saveGridLayoutChangeFailure({ error }));
                  })
               );
         })
      );
   });

   triggersaveSelectedDashboardId = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.deleteDashboardSuccess, DashboardActions.addDashboardSuccess),
         withLatestFrom(this.store.pipe(select(getSelectedDashboard))),
         map(([action, selectedDashboard]) => {
            return UserPreferenceActions.saveSelectedDashboardId({
               selectedDashboardId: selectedDashboard?.dashboardId ?? null,
            });
         })
      );
   });

   updateDashboardTileOnSelectedDashboardById = createEffect(() => {
      return this.actions$.pipe(
         ofType(DashboardActions.updateDashboardTileOnSelectedDashboardById),
         withLatestFrom(this.store.pipe(select(getSelectedDashboard))),
         mergeMap(([action, selectedDashboard]) => {
            if (selectedDashboard === null) return EMPTY;

            const dashboardTile = selectedDashboard.dashboardTiles.find(
               (d) => d.dashboardTileId === action.dashboardTileId
            );
            if (!dashboardTile) return EMPTY;

            return this.dashboardService.updateDashboardTileOnSelectedDashboard(selectedDashboard, dashboardTile).pipe(
               mergeMap((response) => {
                  const dashboardTile = this.dashboardTileConnectionsToDashboardTiles(
                     response.data.dashboardTilesConnection
                  )[0];
                  return [
                     DashboardActions.updateDashboardTileOnSelectedDashboardByIdSuccess({
                        dashboardTile,
                     }),
                     TileActions.tileLoad({ dashboardTileId: dashboardTile.dashboardTileId }),
                  ];
               }),
               catchError((error) => {
                  return of(DashboardActions.updateDashboardTileOnSelectedDashboardByIdFailure(error));
               })
            );
         })
      );
   });

   constructor(
      private store: Store<AppState>,
      private dashboardService: DashboardApiService,
      private actions$: Actions,
      private dialog: MatDialog
   ) {}

   private mapToDashboards(dashboards: ExecutionResult<GetDashboards>): Dashboard[] {
      return dashboards.data.dashboardsConnection.items.map((d) => {
         return this.mapToDashboard(d);
      });
   }

   private mapToDashboard(dashboard: GetDashboards_dashboardsConnection_items | SaveDashboards_dashboard) {
      return {
         name: dashboard.name,
         dashboardId: dashboard.dashboardId,
         dashboardUserId: dashboard.userId,
         lastSavedColumnLayoutSize: dashboard.lastSavedColumnLayoutSize,
         dashboardTiles: this.dashboardTileConnectionsToDashboardTiles(dashboard.dashboardTilesConnection),
      };
   }

   private dashboardTileConnectionsToDashboardTiles(
      dashboardTilesConnection:
         | GetDashboards_dashboardsConnection_items_dashboardTilesConnection
         | SaveDashboards_dashboard_dashboardTilesConnection
         | GetDashboardTiles_dashboardTilesConnection
   ): DashboardTile[] {
      return dashboardTilesConnection.items.map((dt) => {
         return {
            dashboardTileId: dt.dashboardTileId,
            totalMillisecondsProcessed: dt.totalMillisecondsProcessed,
            totalTimesProcessed: dt.totalTimesProcessed,
            viewMode: dt.tileViewMode ?? TileViewModes.Medium,
            tile: {
               tileId: dt.tile.tileId,
               name: dt.tile.name,
               description: dt.tile.description,
               canEditRefreshTime: dt.tile.canEditRefreshTime,
               typeId: dt.tile.typeId,
               isCustom: dt.tile.isCustom,
               tileViewModeBound: dt.tile.tileViewModeBound
                  ? {
                       heightLowerBound: dt.tile.tileViewModeBound.heightLowerBound,
                       heightUpperBound: dt.tile.tileViewModeBound.heightUpperBound,
                       widthLowerBound: dt.tile.tileViewModeBound.widthLowerBound,
                       widthUpperBound: dt.tile.tileViewModeBound.widthUpperBound,
                    }
                  : null,
            },
            dashboardTileLayoutConfiguration: {
               dashboardTileLayoutConfigurationId:
                  dt.dashboardTileLayoutConfiguration!.dashboardTileLayoutConfigurationId,
               color: dt.dashboardTileLayoutConfiguration!.color,
               height: dt.dashboardTileLayoutConfiguration!.height,
               width: dt.dashboardTileLayoutConfiguration!.width,
               refreshTimeSeconds: dt.dashboardTileLayoutConfiguration!.refreshTimeSeconds,
               maxHeight: dt.dashboardTileLayoutConfiguration!.maxHeight,
               minHeight: dt.dashboardTileLayoutConfiguration!.minHeight,
               maxWidth: dt.dashboardTileLayoutConfiguration!.maxWidth,
               minWidth: dt.dashboardTileLayoutConfiguration!.minWidth,
               name: dt.dashboardTileLayoutConfiguration!.name,
               x: dt.dashboardTileLayoutConfiguration!.column,
               y: dt.dashboardTileLayoutConfiguration!.row,
            },
            dashboardTileParameters: [
               ...this.dashboardTileParametersConnectionToReportParametersUnion(
                  dt.dashboardTileParametersConnection!.items
               ),
            ],
         };
      });
   }

   private dashboardTileParametersConnectionToReportParametersUnion(
      parameterConnections:
         | GetDashboards_dashboardsConnection_items_dashboardTilesConnection_items_dashboardTileParametersConnection_items[]
   ): ReportParametersUnion[] {
      const parameterUnions: ReportParametersUnion[] = [];
      for (let i = 0; i < parameterConnections.length; i++) {
         const p = parameterConnections[i];
         switch (p.parameterType) {
            case ReportParameters.ExtensionsToInclude:
               const ids = p.entitiesToInclude?.entityIdsConnection.items.map((e) => e.entityId) ?? [];
               const all = p?.entitiesToInclude?.all ?? false;
               const mappedUnion: ReportParametersUnion = {
                  all,
                  values: ids,
                  names: [],
                  parameterType: p.parameterType,
                  sortOrder: i,
               };
               parameterUnions.push({ ...mappedUnion });
               break;
            case ReportParameters.CallQueueExtensionsToInclude:
               const cqIds = p.entitiesToInclude?.entityIdsConnection.items.map((e) => e.entityId) ?? [];
               const cqAll = p?.entitiesToInclude?.all ?? false;
               const cqMappedUnion: ReportParametersUnion = {
                  all: cqAll,
                  values: cqIds,
                  names: [],
                  parameterType: p.parameterType,
                  sortOrder: i,
               };
               parameterUnions.push({ ...cqMappedUnion });
               break;
            case ReportParameters.TimeZone:
               parameterUnions.push({
                  parameterType: p.parameterType,
                  sortOrder: i,
                  value: p.valueString ?? '',
               });
               break;
            case ReportParameters.TimeRange:
               parameterUnions.push({
                  parameterType: p.parameterType,
                  sortOrder: i,
                  startDate: p.dateRange?.startDate,
                  endDate: p.dateRange?.endDate,
                  startTime: p.dateRange?.startDate ?? null,
                  endTime: p.dateRange?.endDate ?? null,
               });
               break;
            case ReportParameters.SelectedAccount:
               parameterUnions.push({
                  parameterType: p.parameterType,
                  sortOrder: i,
                  value: p.valueNumeric ?? '',
                  name: p.valueString ?? '',
               });
               break;
            case ReportParameters.ShowDate:
               parameterUnions.push({
                  parameterType: p.parameterType,
                  sortOrder: i,
                  value: p.valueBool || false,
               });
               break;
            case ReportParameters.AggregateQueues:
               parameterUnions.push({
                  parameterType: p.parameterType,
                  sortOrder: i,
                  value: p.valueBool || false,
               });
               break;
            case ReportParameters.ColumnSort:
               parameterUnions.push({
                  parameterType: p.parameterType,
                  sortOrder: i,
                  value: p.valueString || '',
               });
               break;
            default:
               console.error('Invalid report parameter');
               throw new Error('Invalid report parameter');
         }
      }
      return parameterUnions;
   }
}
